/* ---------- Helpers that need no cart events (buy box + drawer) ---------- */

const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
const pad = (n) => String(n).padStart(2, '0');

// Live countdown from a merchant-set end timestamp (seconds) in data-wf-countdown. The server
// renders a fallback; this keeps a cached page honest and hides the timer at zero.
const tickCountdowns = () => {
  const now = Date.now() / 1000;
  for (const el of document.querySelectorAll('[data-wf-countdown]')) {
    const left = Math.floor(Number(el.dataset.wfCountdown) - now);
    if (!(left > 0)) {
      el.hidden = true;
      continue;
    }
    const d = Math.floor(left / 86400);
    const h = Math.floor((left % 86400) / 3600);
    const m = Math.floor((left % 3600) / 60);
    const text = `${d}d ${pad(h)}h ${pad(m)}m ${pad(left % 60)}s`;
    const out = el.querySelector('[data-wf-countdown-out]');
    if (out && out.textContent !== text) out.textContent = text;
  }
};

// Delivery window counted from today, from the stated min/max days only.
const formatDay = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const applyEta = () => {
  for (const el of document.querySelectorAll('[data-wf-eta]:not([data-wf-eta-done])')) {
    const host = el.closest('[data-wf-ship-min]');
    const min = Number(host?.dataset.wfShipMin);
    const max = Number(host?.dataset.wfShipMax);
    if (!(min > 0 && max >= min)) continue;
    const from = new Date();
    from.setDate(from.getDate() + min);
    const to = new Date();
    to.setDate(to.getDate() + max);
    const range = document.createElement('b');
    range.textContent = `${formatDay(from)} – ${formatDay(to)}`;
    el.replaceChildren(`${el.dataset.wfEtaLead || ''} `, range);
    el.dataset.wfEtaDone = '1';
  }
};

tickCountdowns();
applyEta();
setInterval(tickCountdowns, 1000);

// The drawer is re-rendered (morphed) after every cart change, which restores the server text.
const drawerRoot = document.getElementById('cart-drawer');
if (drawerRoot) {
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      applyEta();
      tickCountdowns();
    });
  }).observe(drawerRoot, { childList: true, subtree: true });
}

// At 990px and up the theme locks html/body and scrolls .page-wrapper instead. If anything ever
// scrolls the locked root (a focused off-screen input did, and blanked the page below the buy box),
// put it straight back.
const desktopLock = window.matchMedia('(min-width: 990px)');
window.addEventListener(
  'scroll',
  (event) => {
    if (event.target !== document && event.target !== document.body) return;
    if (!desktopLock.matches || !document.querySelector('.page-wrapper')) return;
    const root = document.documentElement;
    if (root.scrollTop || document.body.scrollTop) {
      root.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  },
  { capture: true, passive: true }
);

// Mobile sticky Add to Cart for the buy box.
const sticky = document.getElementById('wf-sticky-atc');
const stickyRoot = sticky && document.getElementById(`wf-${sticky.dataset.section}`);
if (sticky && stickyRoot && 'IntersectionObserver' in window) {
  const mobile = window.matchMedia('(max-width: 749px)');
  const seen = new Map();
  const nameEl = sticky.querySelector('.wf-sticky-atc__name');
  const priceEl = sticky.querySelector('.wf-sticky-atc__price');
  const imgEl = sticky.querySelector('.wf-sticky-atc__img');
  const addBtn = sticky.querySelector('[data-wf-sticky-add]');
  const visibleForm = () => [...stickyRoot.querySelectorAll('.wf-buy__form')].find((f) => f.offsetParent !== null);
  const selectedCard = (form) =>
    form?.closest('.wf-buy__pane')?.querySelector('.wf-buy__tier input:checked + .wf-buy__tier-card');

  const update = () => {
    const form = visibleForm();
    const cta = form?.querySelector('.wf-buy__cta');
    const on = Boolean(mobile.matches && cta && seen.get(cta) === false);
    if (on) {
      const card = selectedCard(form);
      nameEl.textContent = card?.querySelector('.wf-buy__tier-name')?.textContent.trim() || '';
      const grand = form.querySelector('.wf-buy__grand > span:last-child');
      const shown = grand && [...grand.querySelectorAll('.wf-buy__t')].find((t) => t.offsetParent !== null);
      priceEl.textContent = (shown || grand)?.textContent.trim() || '';
      const src = card?.querySelector('.wf-buy__tier-img img');
      const current = imgEl.querySelector('img');
      if (!src) imgEl.replaceChildren();
      else if (!current || current.getAttribute('src') !== src.getAttribute('src')) {
        const img = src.cloneNode();
        img.loading = 'eager';
        imgEl.replaceChildren(img);
      }
      addBtn.disabled = cta.disabled;
    }
    sticky.classList.toggle('is-on', on);
    sticky.inert = !on;
    document.documentElement.classList.toggle('wf-sticky-on', on);
    if (on) document.documentElement.style.setProperty('--wf-sticky-h', `${sticky.offsetHeight}px`);
  };

  const io = new IntersectionObserver((entries) => {
    for (const entry of entries) seen.set(entry.target, entry.isIntersecting);
    update();
  });
  for (const cta of stickyRoot.querySelectorAll('.wf-buy__form .wf-buy__cta')) io.observe(cta);
  stickyRoot.addEventListener('change', () => requestAnimationFrame(update));
  mobile.addEventListener('change', update);

  addBtn.addEventListener('click', () => {
    const form = visibleForm();
    const cta = form?.querySelector('.wf-buy__cta');
    if (form && cta && !cta.disabled) form.requestSubmit(cta);
  });
  sticky.querySelector('[data-wf-sticky-jump]').addEventListener('click', () => {
    visibleForm()
      ?.closest('.wf-buy__pane')
      ?.querySelector('.wf-buy__tiers')
      ?.scrollIntoView({ behavior: reduceMotionQuery.matches ? 'auto' : 'smooth', block: 'start' });
  });
}

let events;
try {
  events = await import('@shopify/events');
} catch (error) {
  events = null;
}

// `Theme` is a global const (not a window property), so it is read by bare name.
const routes = typeof Theme === 'undefined' ? null : Theme.routes;

if (events && routes && routes.cart_add_url) {
  const { CartLinesUpdateEvent, StandardEvents } = events;
  const fab = document.getElementById('wf-cart-fab');
  const fabCount = document.getElementById('wf-cart-fab-count');
  const getDrawer = () => document.getElementById('cart-drawer');

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const setCount = (count) => {
    if (!fab || !fabCount) return;
    fabCount.textContent = String(count);
    fabCount.hidden = count < 1;
    fab.hidden = count < 1 && !fab.hasAttribute('data-wf-always');
  };

  // The cart drawer is opened by the customer (cart button in the header or the floating button),
  // never automatically after an add, so the item can fly into the floating button first.
  document.querySelector('cart-drawer-component')?.removeAttribute('auto-open');

  const bump = () => {
    if (!fab || fab.hidden || reduceMotion.matches) return;
    fab.animate(
      [
        { transform: 'scale(1)' },
        { transform: 'scale(1.3)', offset: 0.35 },
        { transform: 'scale(0.9)', offset: 0.65 },
        { transform: 'scale(1)' },
      ],
      { duration: 480, easing: 'ease-out' }
    );
    fabCount?.animate(
      [{ transform: 'scale(1)' }, { transform: 'scale(1.5)', offset: 0.4 }, { transform: 'scale(1)' }],
      { duration: 480, easing: 'ease-out' }
    );
  };

  const wiggle = () => {
    if (!fab || fab.hidden || reduceMotion.matches || document.hidden) return;
    if (getDrawer()?.hasAttribute('open') || fabCount?.hidden) return;
    fab.animate(
      [
        { transform: 'rotate(0deg)' },
        { transform: 'rotate(-14deg)', offset: 0.2 },
        { transform: 'rotate(12deg)', offset: 0.4 },
        { transform: 'rotate(-8deg)', offset: 0.6 },
        { transform: 'rotate(5deg)', offset: 0.8 },
        { transform: 'rotate(0deg)' },
      ],
      { duration: 700, easing: 'ease-in-out' }
    );
  };

  // A small reminder every 5 seconds that there is a cart waiting.
  setInterval(wiggle, 5000);

  /**
   * Flies a small copy of the product image (or, without one, an orange dot) from the buy box into
   * the floating cart button, then bumps the button. Resolves when it has landed.
   */
  const flyToCart = async (form) => {
    if (!fab || fab.hidden || reduceMotion.matches) return;
    // Adds made from inside the open drawer have nowhere visible to fly to.
    if (getDrawer()?.hasAttribute('open')) return;

    // The selected option card's own thumbnail (a bundle shows the bundle), then the gallery.
    const thumb = form
      .closest('.wf-buy__pane')
      ?.querySelector('.wf-buy__tier input:checked + .wf-buy__tier-card .wf-buy__tier-img img');
    const images = [thumb, ...document.querySelectorAll('.wf-buy__stage img')].filter(Boolean);
    const inView = (rect) =>
      rect.width > 0 && rect.height > 0 && rect.bottom > 40 && rect.top < window.innerHeight - 40;
    // Fly from the product image when it is on screen, otherwise from the Add to Cart button,
    // still wearing the product photo.
    const visibleImage = images.find((img) => img.offsetParent !== null && inView(img.getBoundingClientRect()));
    const image = thumb || visibleImage || images.find((img) => img.currentSrc || img.src);
    // With the sticky bar up, the real CTA is off-screen: launch from the bar instead.
    const stickyOn = document.querySelector('.wf-sticky-atc.is-on');
    const source = (
      visibleImage ||
      stickyOn?.querySelector('.wf-sticky-atc__img img, .wf-sticky-atc__cta') ||
      form.querySelector('.wf-buy__cta')
    )?.getBoundingClientRect();
    if (!source) return;

    const size = 96;
    const target = fab.getBoundingClientRect();
    const startX = source.left + source.width / 2 - size / 2;
    const startY = source.top + source.height / 2 - size / 2;
    const endX = target.left + target.width / 2 - size / 2;
    const endY = target.top + target.height / 2 - size / 2;
    const midX = startX + (endX - startX) * 0.55;
    const midY = Math.min(startY, endY) - 70;

    const ghost = document.createElement('div');
    ghost.setAttribute('aria-hidden', 'true');
    ghost.style.cssText = [
      'position:fixed',
      'left:0',
      'top:0',
      `width:${size}px`,
      `height:${size}px`,
      'z-index:95',
      'pointer-events:none',
      'border-radius:18px',
      'box-shadow:0 10px 30px rgb(0 0 0 / 0.25)',
      'will-change:transform,opacity',
      image
        ? `background:#fff url("${image.currentSrc || image.src}") center / contain no-repeat`
        : 'background:#ef6820',
    ].join(';');
    document.body.appendChild(ghost);

    const animation = ghost.animate(
      [
        { transform: `translate(${startX}px, ${startY}px) scale(1)`, opacity: 1 },
        { transform: `translate(${midX}px, ${midY}px) scale(0.7)`, opacity: 1, offset: 0.45 },
        { transform: `translate(${endX}px, ${endY}px) scale(0.22)`, opacity: 0.9 },
      ],
      { duration: 800, easing: 'cubic-bezier(0.45, 0.05, 0.25, 1)', fill: 'forwards' }
    );

    try {
      await animation.finished;
    } catch (error) {
      // Cancelled (for example the page was hidden); nothing to clean up beyond the ghost.
    }
    ghost.remove();
    bump();
  };

  const syncCount = () =>
    fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((response) => response.json())
      .then((cart) => setCount(cart.item_count))
      .catch(() => {});

  fab?.addEventListener('click', () => getDrawer()?.open());

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    event.promise?.then(syncCount).catch(() => {});
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) syncCount();
  });

  const showError = (form, message) => {
    let el = form.querySelector('.wf-buy__err');
    if (!el) {
      el = document.createElement('p');
      el.className = 'wf-buy__err';
      el.setAttribute('role', 'alert');
      const anchor = form.querySelector('.wf-buy__cta');
      if (anchor) anchor.insertAdjacentElement('beforebegin', el);
      else form.append(el);
    }
    el.textContent = message;
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('wf-buy__form')) return;
    if (!getDrawer()) return;

    event.preventDefault();
    if (form.dataset.wfBusy) return;
    form.dataset.wfBusy = '1';

    const button = form.querySelector('.wf-buy__cta, button[type="submit"]');
    button?.setAttribute('aria-busy', 'true');
    form.querySelector('.wf-buy__err')?.remove();

    const formData = new FormData(form);
    const lines = [];
    for (let i = 0; formData.has(`items[${i}][id]`); i++) {
      lines.push({
        merchandiseId: String(formData.get(`items[${i}][id]`)),
        quantity: Number(formData.get(`items[${i}][quantity]`)) || 1,
      });
    }

    const sectionIds = [
      ...new Set(
        [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean)
      ),
    ];
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    form.dispatchEvent(
      new CartLinesUpdateEvent({ action: 'add', context: 'product', lines, promise: deferred.promise })
    );

    let nativeFallback = false;
    let added = false;
    try {
      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      });
      const data = await response.json();
      added = response.ok && !data.status;

      if (!response.ok || data.status) {
        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'wf-buybox', sourceId: form.id },
        });
        showError(form, data.description || data.message || 'Sorry, that could not be added to your cart.');
      } else {
        let cart = null;
        let sections = data.sections;
        const replaceKey = form.dataset.wfReplaceKey;
        if (replaceKey && routes.cart_change_url) {
          try {
            const quantity = Math.max(0, Number(form.dataset.wfReplaceQty) || 0);
            const change = await fetch(routes.cart_change_url, {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: replaceKey, quantity, sections: sectionIds.join(',') }),
            });
            const changed = await change.json();
            if (change.ok && !changed.status) {
              cart = changed;
              sections = changed.sections || sections;
              const removal = CartLinesUpdateEvent.createPromise();
              removal.promise.catch(() => {});
              form.dispatchEvent(
                new CartLinesUpdateEvent({
                  action: quantity > 0 ? 'update' : 'remove',
                  context: 'cart',
                  lines: [{ id: replaceKey, quantity }],
                  promise: removal.promise,
                })
              );
              removal.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(changed),
                detail: { items: changed.items, source: 'wf-cart-extras', sections, didError: false },
              });
            }
          } catch (error) {
            // The bundle is in; the fountain line simply stays for the shopper to remove.
          }
        }
        cart = cart || (await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()));
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'wf-buybox',
            sourceId: form.id,
            itemCount: lines.reduce((sum, line) => sum + line.quantity, 0),
            sections,
            didError: false,
          },
        });
        setCount(cart.item_count);
        flyToCart(form);
      }
    } catch (error) {
      deferred.reject(error);
      // Never repeat an add that already went through.
      nativeFallback = !added;
    } finally {
      button?.removeAttribute('aria-busy');
      delete form.dataset.wfBusy;
    }

    // Network or parsing failure: fall back to the original full-page add so the customer is never stuck.
    if (nativeFallback) form.submit();
  });
}